import { EventArtifacts, EventTypeIdentifier, FleetUUIDs, VideoStatus } from '../enums';
import { Event, EventVideo } from '../interfaces';
import { isAggregableEvent } from './isAggregableEvent';
import { isContextCamera } from './isContextCamera';
import { isPreImage } from './isPreImage';

export const getEventVideos = (event: Event): EventVideo[] => {
  let videos = event.event_videos ?? event.sub_events?.[0].event_videos ?? [];

  if (isAggregableEvent(event)) {
    const { sub_events } = event;

    if (sub_events) {
      videos = event.sub_events?.[0].event_videos ?? event.event_videos ?? [];
    } else {
      // special case for legacy events with manually changed type
      videos = event.event_videos ?? [];
    }
  }

  return videos;
};

export const eventHasVideos = (event: Event): boolean => {
  const videos = getEventVideos(event);
  const hasArtifacts = !!event.event_artifacts;
  const hasArtifact = event.event_artifacts?.some((a) => {
    return a.is_present && (a.kind === EventArtifacts.ContextVideo || a.kind === EventArtifacts.LPRVideo);
  });

  // FIXME remove this when event_artifacts are exposed in the backend for non-internal users
  if (event.event_type_identifier === EventTypeIdentifier.ROADWORKS) {
    return false;
  }

  return videos.length > 0 && videos[0].status === VideoStatus.publishedStr && (!hasArtifacts || !!hasArtifact);
};

export const getEventThumbnailURL = (event: Event): string => {
  const videos = getEventVideos(event);

  return videos[0]?.video_thumbnail_url ?? undefined;
};

export const getPreImageThumbnailURL = (event: Event): string | undefined => {
  const videos = getEventVideos(event);

  return videos[0]?.pre_image_thumbnail_url ?? undefined;
};

export const getEventLicensePlateURL = (event: Event): string => {
  const videos = getEventVideos(event);

  if (isContextCamera(event)) {
    return videos[0]?.video_thumbnail_url ?? undefined;
  }

  return videos[0]?.license_plate_url ?? undefined;
};

export const getEventRawLicensePlateURL = (event: Event): string => {
  const videos = getEventVideos(event);

  if (isContextCamera(event)) {
    return videos[0]?.video_thumbnail_url ?? undefined;
  }

  return videos[0]?.raw_license_plate_url ?? undefined;
};

export const selectEventThumbnailUrl = (event: Event, isInternal: boolean): string => {
  if (isPreImage(event, isInternal)) {
    return getPreImageThumbnailURL(event) ?? getEventThumbnailURL(event);
  }

  return getEventThumbnailURL(event);
};

export const getEventImageSize = (event: Event): { width: number; height: number } => {
  const isGen3 = event?.camera_version_name?.includes('gen3');

  if (isGen3) {
    return { width: 3840, height: 2170 };
  }

  return { width: 1920, height: 1080 };
};

export const getEventLPRImageSize = (event?: Event): { width: number; height: number } => {
  const isGen3 = event?.camera_version_name?.includes('gen3');

  if (isGen3) {
    return { width: 2592, height: 1946 };
  }

  return { width: 1600, height: 1300 };
};

export const getHasLicensePlateImage = (event: Event): boolean => {
  if (event?.event_type_identifier === EventTypeIdentifier.ROADWORKS) {
    return false;
  }

  if (event?.fleet_id === FleetUUIDs.SacRTBusStopShelter || event?.fleet_id === FleetUUIDs.SacRTBusStopShelterActivePilot) {
    return false;
  }

  return true;
};

export const getHasLicensePlateText = (event: Event): boolean => {
  if (event?.fleet_id === FleetUUIDs.SacRTBusStopShelter || event?.fleet_id === FleetUUIDs.SacRTBusStopShelterActivePilot) {
    return false;
  }

  return true;
};

export const getHasBlockName = (event: Event): boolean => {
  if (event?.event_type_identifier === EventTypeIdentifier.BUS_LANE_MATCH) {
    return false;
  }

  return !event?.event_type_identifier?.includes(EventTypeIdentifier.ROADWORKS);
};
